import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Mui from 'materials';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import TextField from 'components/text-field';
import LoadingSpinner from 'components/loading-spinner';

import { PutSiloSettingsRequest } from 'views/site-settings/features/inventory-silo-management/types/put-silo-settings-request.type';

import { usePost } from 'hooks/use-post';
import ActionButtons from 'components/action-buttons';
import { useSiteSettingsState } from 'views/site-settings/context/site-settings-context';
import { putSiloSettings } from '../api/silo-management';
import { useAccountState } from 'contexts/account/account-context';
import { useUserFeatures } from 'views/site-settings/hooks/use-user-features';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.siloCapacity';
const EPOCH_TIME = 19748; // January 1, 1970 EPOCH time

const SiloCapacity: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const { canEditSiteSettings } = useUserFeatures();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    siteSettingsQuery: { data: settings, isLoading },
  } = useSiteSettingsState();

  if (isLoading) return <LoadingSpinner />; // bad solution?
  if (!settings) return <></>;

  //#region STATE
  const [edit, setEdit] = useState(false);
  //#endregion

  const validateDate = (edit: boolean, date: string) => {
    if (dayjs(date).isAfter(EPOCH_TIME)) return date;
    return edit && dayjs(date).isBefore(EPOCH_TIME) ? dayjs().toString() : '';
  };

  // #region FORM
  const { handleSubmit, control, reset, setValue, getValues } = useForm<PutSiloSettingsRequest>({
    defaultValues: useMemo(() => {
      return {
        siloFeedQuantityDate: validateDate(edit, settings.siloFeedQuantityDate),
        siloFeedQuantity: settings.siloFeedQuantity,
      };
    }, [settings, edit]),
    shouldUnregister: true,
  });
  // #endregion FORM

  // #region POST DATA
  const updateSiloCapacity = usePost<AxiosResponse, PutSiloSettingsRequest>(
    (request) => putSiloSettings(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEdit(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      setEdit(false);
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleUpdateSiloCapacity = (request: PutSiloSettingsRequest) => {
    updateSiloCapacity.mutate({
      ...request,
      siloFeedQuantityDate: dayjs(request.siloFeedQuantityDate).format('YYYY-MM-DD'),
    });
  };

  const handleEdit = (edited: boolean) => {
    setEdit(edited);
    setValue('siloFeedQuantityDate', !edit ? dayjs().format('YYYY-MM-DD') : validateDate(!edit, settings.siloFeedQuantityDate));
  };

  const handleCancel = () => {
    reset();
    setEdit(false);
  };

  // #endregion HANDLERS

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.siloSiteInventory` })} open hideBorder>
      <form onSubmit={handleSubmit(handleUpdateSiloCapacity)}>
        <Grid container spacing={6} pb={4} px={isMobile ? 4 : 0}>
          <Grid size={12}>
            <ActionButtons
              isDisabled={canEditSiteSettings}
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSubmit(handleUpdateSiloCapacity)}
            />
          </Grid>
          {updateSiloCapacity.isPending && <LoadingSpinner overlay />}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              dark
              fullWidth
              type="number"
              disabled={!edit}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.siloCurrentInventoryLabel` })}
              endAdornment={<>kg</>}
              controller={{
                name: 'siloFeedQuantity',
                control: control,
                defaultValue: 0,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              dark
              fullWidth
              disabled={!edit}
              disableFuture={true}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.siloInventoryUpdatedLabel` })}
              controller={{
                name: 'siloFeedQuantityDate',
                control: control,
                rules: {
                  required: true,
                  validate: {
                    isValidDate: (date) => dayjs(date).isValid(),
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </form>
    </Collapsible>
  );
};

export default React.memo(SiloCapacity);
